// src/components/contacts/ContactActivityDrawer.tsx
import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Button, Space, DatePicker, message, Segmented, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { createContactActivity } from '../../services/contacts';
import { listContactActivityTypes, type ContactActivityTypeDTO } from '../../services/contactActivityTypes';
import { renderAntdIcon } from '../../utils/antdIcon';

type Props = {
  open: boolean;
  onClose: () => void;
  contactId: number | null;
  onCreated?: () => void;
  currentUserId?: number; // opzionale
};

type FormValues = {
  typeId?: number;
  notes?: string;        // <<-- corretto: "notes"
  occurredAt?: string;   // ISO
};

const ContactActivityDrawer: React.FC<Props> = ({ open, onClose, contactId, onCreated, currentUserId }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<ContactActivityTypeDTO[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const list = await listContactActivityTypes();
        const active = (list || []).filter(t => t.active);
        setTypes(active);
        const defId = active?.[0]?.id;
        setSelectedTypeId(defId);
        form.setFieldsValue({
          typeId: defId,
          occurredAt: dayjs().toISOString(),
        });
      } catch (e: any) {
        message.error(e?.message || 'Errore caricamento tipi attività');
      }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!contactId) return;
    try {
      const values = await form.validateFields();
      if (!values.typeId) return message.warning('Seleziona il tipo attività');
      setLoading(true);
      await createContactActivity(contactId, {
        typeId: values.typeId,
        notes: values.notes,  // <<-- usa notes
        occurredAt: values.occurredAt || new Date().toISOString(),
        userId: currentUserId, // opzionale
      });
      message.success('Attività aggiunta');
      form.resetFields();
      onCreated?.();
      onClose();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Nuova attività"
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="primary" onClick={handleSave} loading={loading}>
            Salva
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" initialValues={{ occurredAt: dayjs().toISOString() }}>
        <Form.Item name="typeId" label="Tipo" rules={[{ required: true, message: 'Seleziona il tipo attività' }]}>
          <Segmented<number>
            value={selectedTypeId}
            onChange={(val) => {
              setSelectedTypeId(val);
              form.setFieldValue('typeId', val);
            }}
            options={types.map(t => ({
              label: (
                <Tooltip title={t.label}>
                  <Space>
                    <span style={{ fontSize: 16 }}>{renderAntdIcon(t.icon)}</span>
                    <span>{t.label}</span>
                  </Space>
                </Tooltip>
              ),
              value: t.id!,
            }))}
            block
          />
        </Form.Item>

        <Form.Item
          name="occurredAt"
          label="Data/Ora"
          getValueFromEvent={(d: Dayjs | null) => (d ? d.toISOString() : undefined)}
          getValueProps={(v?: string) => ({ value: v ? dayjs(v) : undefined })}
        >
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="notes" label="Note">
          <Input.TextArea placeholder="Esito, dettagli, prossimi passi…" autoSize={{ minRows: 4 }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ContactActivityDrawer;
